import type { Content, TableCell, TableLayout } from 'pdfmake/interfaces';
import type { ViewBlock } from '@/data/schemas';
import { cm, pdfColors, tableLayouts } from '../../theme.pdf';
import { BLOCK_SPACING } from '../layout';

type RowGroupBlock = Extract<ViewBlock, { type: 'row-group' }>;

type LayoutKey = keyof typeof tableLayouts;

const LABEL_COL_WIDTH_CM = 4.6;
const ROW_LABEL_COL_WIDTH_CM = 4.0;

export function renderRowGroupBlock(
  block: RowGroupBlock,
  options: { layout?: LayoutKey; marginBottom?: number } = {}
): Content {
  const layoutKey = options.layout ?? 'default';
  const marginBottom = options.marginBottom ?? BLOCK_SPACING;

  const body: TableCell[][] = [];
  const separatorLines = new Set<number>();

  block.rows.forEach((row) => {
    if (body.length > 0) {
      separatorLines.add(body.length);
    }
    const items = row.items.length > 0 ? row.items : [{ label: '', value: '—' }];
    const rowSpanCount = items.length + (row.description ? 1 : 0);

    items.forEach((item, itemIndex) => {
      const rowCells: TableCell[] = [];

      if (itemIndex === 0) {
        rowCells.push({
          text: row.label,
          style: 'rowGroupLabel',
          rowSpan: rowSpanCount,
          margin: [0, 0, 0, 0],
        });
      } else {
        rowCells.push({});
      }

      rowCells.push({
        text: item.label,
        style: 'tableCellLabel',
        fillColor: item.highlight ? pdfColors.yellow : undefined,
      } satisfies TableCell);

      rowCells.push({
        text: item.value ?? '—',
        style: 'tableCell',
        fillColor: item.highlight ? pdfColors.yellow : undefined,
      } satisfies TableCell);

      body.push(rowCells);
    });

    if (row.description) {
      body.push([
        {},
        {
          text: row.description,
          style: 'rowGroupDescription',
          colSpan: 2,
          margin: [0, 0, 0, 0],
        } satisfies TableCell,
        {},
      ]);
    }
  });

  const baseLayout = tableLayouts[layoutKey] as Exclude<(typeof tableLayouts)[LayoutKey], string>;

  const rowGroupLayout = {
    hLineWidth: (i: number) => (separatorLines.has(i) ? 0.8 : 0.5),
    vLineWidth: () => 0.5,
    hLineColor: () => pdfColors.black,
    vLineColor: () => pdfColors.black,
    paddingLeft: baseLayout.paddingLeft,
    paddingRight: baseLayout.paddingRight,
    paddingTop: baseLayout.paddingTop,
    paddingBottom: baseLayout.paddingBottom,
  };

  return {
    table: {
      widths: [cm(ROW_LABEL_COL_WIDTH_CM), cm(LABEL_COL_WIDTH_CM), '*'],
      dontBreakRows: true,
      body,
    },
    layout: rowGroupLayout as TableLayout,
    margin: [0, 0, 0, marginBottom],
  };
}
